import { getItem, setItem, removeItem, key as storageKey } from './storage';

type WebStorageType = 'localStorage' | 'sessionStorage';

export class WebStorage<StorageMap = Record<string, any>> {
  readonly namespace?: string;

  readonly type: WebStorageType;

  private readonly storage: Storage = window.localStorage;

  constructor(props?: { namespace?: string; type?: WebStorageType }) {
    const { namespace, type = 'localStorage' } = props || {};

    this.namespace = namespace;
    this.storage = window[type];
  }

  private get preKey() {
    return this.namespace ? `${this.namespace}:` : '';
  }

  private keyWithNs<keyStr extends keyof StorageMap>(key: keyStr): string {
    return `${this.preKey}${key}`;
  }

  private keyWithoutNs(key?: string | null): string {
    const inNamespace = Boolean(
      key && this.namespace && key.startsWith(this.preKey),
    );
    if (inNamespace) {
      return key?.replace(this.preKey, '') || '';
    }
    return '';
  }

  getItem<keyStr extends keyof StorageMap>(key: keyStr): StorageMap[keyStr] {
    return getItem(this.storage, this.keyWithNs(key));
  }

  setItem<keyStr extends keyof StorageMap>(
    key: keyStr,
    data: StorageMap[keyStr],
  ) {
    setItem(this.storage, this.keyWithNs(key), data);
  }

  removeItem<keyStr extends keyof StorageMap>(key: keyStr) {
    removeItem(this.storage, this.keyWithNs(key));
  }

  forEach(callback: (key: string, data: any, originalKey: string) => void) {
    const len = this.storage.length;
    for (let i = 0; i < len; i++) {
      const originalKey = storageKey(this.storage, i);
      const key = this.keyWithoutNs(originalKey);
      if (!originalKey || !key) {
        continue;
      }
      callback(key, getItem(this.storage, originalKey), originalKey);
    }
  }

  clear(): void {
    if (!this.namespace) {
      return;
    }
    this.forEach((_key, _data, originalKey) => {
      removeItem(this.storage, originalKey);
    });
  }

  getAll(): Record<string, any> | undefined {
    if (!this.namespace) {
      return undefined;
    }
    let result: Record<string, any> | undefined;
    this.forEach((key, data) => {
      result = result || {};
      result[key] = data;
    });
    return result;
  }
}
